/**
 * Единственное место, где разрешено трогать Date: форматирование уже готовой
 * метки времени. Бизнес-код часы не читает — время приходит снаружи
 * (06-testing-and-coverage.md §1.5), поэтому здесь нет ни `now`, ни таймзон
 * кроме отображаемой.
 */
import { displayLocale } from './locale'

/** Русская локаль — для админки и мест, где язык не переключается. */
export const DISPLAY_LOCALE = 'ru-RU'

/** «12 сентября» · «12-sentabr» */
export function formatDay(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(displayLocale(), { day: 'numeric', month: 'long' })
}

/** «14:30» */
export function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString(displayLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** «12 сентября, 14:30» */
export function formatDayTime(timestamp: number): string {
  return `${formatDay(timestamp)}, ${formatTime(timestamp)}`
}

/**
 * Текущее время. Единственная точка, где приложение узнаёт «сейчас»; всё
 * остальное принимает метку снаружи и потому тестируется без подмены часов.
 */
export function nowMs(): number {
  return Date.now()
}

/**
 * Метка времени из ответа API в миллисекундах. Даты нет или она непонятна —
 * ноль: `NaN` протёк бы в форматирование и вылез на экране как «Invalid Date».
 */
export function parseMs(value: string | null | undefined): number {
  if (!value) return 0
  const ms = Date.parse(value)
  return Number.isNaN(ms) ? 0 : ms
}

/** Метка времени в ISO 8601 (UTC) — так даты уходят в API. */
export function isoString(timestamp: number): string {
  return new Date(timestamp).toISOString()
}

/** Полночь того же дня. Границы суток нужны отчётам: день — единица периода. */
export function startOfDayMs(timestamp: number): number {
  const date = new Date(timestamp)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

/** Сдвиг по календарным дням: переход на летнее время не теряет сутки. */
export function addDaysMs(timestamp: number, days: number): number {
  const date = new Date(timestamp)
  date.setDate(date.getDate() + days)
  return date.getTime()
}

/** «2026-09-22» — значение для <input type="date"> в местном времени. */
export function isoDay(timestamp: number): string {
  const date = new Date(timestamp)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Разбор значения <input type="date"> в местную полночь; мусор → null.
 * Дата проверяется обратной сборкой: «2026-02-31» Date молча превратит в
 * 3 марта, а для фильтра периода это не та дата, которую ввели.
 */
export function parseIsoDay(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const [, year, month, day] = match
  const date = new Date(Number(year), Number(month) - 1, Number(day))
  return isoDay(date.getTime()) === value ? date.getTime() : null
}

/** «2026-09-22T14:30» — значение для <input type="datetime-local"> в местном времени. */
export function localDateTime(timestamp: number): string {
  const date = new Date(timestamp)
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${isoDay(timestamp)}T${hours}:${minutes}`
}

/** Разбор значения <input type="datetime-local">; мусор или несуществующая дата → null. */
export function parseLocalDateTime(value: string): number | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(value)
  if (!match) return null
  const [, day, hours, minutes] = match
  const midnight = parseIsoDay(day as string)
  if (midnight === null || Number(hours) > 23 || Number(minutes) > 59) return null
  const date = new Date(midnight)
  date.setHours(Number(hours), Number(minutes))
  return date.getTime()
}

/** «22.09.2026» */
export function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(displayLocale())
}

/** «22 сент.» — подпись оси и колонки таблицы, где длинный месяц не влезает. */
export function formatShortDay(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(displayLocale(), { day: 'numeric', month: 'short' })
}

/** «22.9» — самая короткая подпись, для частых делений оси. */
export function formatNumericDay(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(displayLocale(), {
    day: 'numeric',
    month: 'numeric',
  })
}
